import React, { useState, useEffect } from "react";
import {
  Button,
  Icon,
  Table,
  Modal,
  Input,
  Message,
  Loader,
} from "semantic-ui-react";
import { map } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks";
import {
  getServerBackupsApi,
  restoreBackupApi,
  saveServerBackupApi,
} from "../../api/backup";

const CONFIRM_WORD = "RESTAURAR";
// Encabezado fijo al deslizar la lista (se ven ~4 respaldos a la vez).
const STICKY = { position: "sticky", top: 0, zIndex: 1 };

const KIND_TEXT = {
  nightly: "Automático",
  manual: "Manual",
  safety: "Antes de restaurar",
};
const LIMA_OFFSET = -5 * 60;

// Fechas del servidor: `created_at` viene con zona; `last_sale` es la hora UTC
// guardada en SQLite ("2026-10-14 21:30:00"). Se muestran en hora de Lima.
const formatWhen = (value, utc = false) =>
  value
    ? (utc ? moment.utc(value) : moment.parseZone(value))
        .utcOffset(LIMA_OFFSET)
        .format("DD/MM/YYYY HH:mm")
    : "—";

const describe = (data) => {
  const c = data.counts || {};
  return `${c.orders} pedidos · ${c.payments} ventas · ${c.products} productos · ${c.users} usuarios`;
};

// Configuración → Respaldo → Restaurar (solo Súper Usuario: la página ya lo es).
// Elegir un respaldo del servidor o subir uno; el sistema lo revisa y muestra
// qué tiene; se confirma escribiendo RESTAURAR. Antes de reemplazar, el
// servidor guarda la base actual ("Antes de restaurar"), así se puede deshacer.
export function RestoreBackup() {
  const { auth, logout } = useAuth();
  const [backups, setBackups] = useState(null);
  const [checking, setChecking] = useState(false);
  const [candidate, setCandidate] = useState(null); // {source, data}
  const [word, setWord] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [saving, setSaving] = useState(false);

  const isSuper = !!auth?.me?.is_superuser;

  const loadBackups = () =>
    getServerBackupsApi(auth.token)
      .then(setBackups)
      .catch(() => setBackups([]));

  useEffect(() => {
    if (isSuper) loadBackups();
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await saveServerBackupApi(auth.token);
      toast.success("Respaldo guardado en el servidor");
      await loadBackups();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const check = async (source) => {
    setChecking(true);
    try {
      const data = await restoreBackupApi(source, null, auth.token);
      setWord("");
      setCandidate({ source, data });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setChecking(false);
    }
  };

  const onFile = (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (file) check({ file });
  };

  const onRestore = async () => {
    setRestoring(true);
    try {
      await restoreBackupApi(candidate.source, CONFIRM_WORD, auth.token);
      setCandidate(null);
      toast.success("Respaldo restaurado. Vuelve a iniciar sesión.");
      // Los usuarios pueden ser otros en el respaldo: se entra de nuevo.
      setTimeout(() => {
        logout();
        window.location.href = "/admin";
      }, 1500);
    } catch (error) {
      toast.error(error.message);
      setRestoring(false);
    }
  };

  // La página de Respaldo es del Súper Usuario; por si un Admin llega con la URL.
  if (!isSuper) return null;

  return (
    <div className="restore-backup">
      <h4 style={{ margin: "36px 0 6px" }}>Restaurar un respaldo</h4>
      <p>
        Reemplaza <strong>toda</strong> la información actual por la de un
        respaldo. Antes de hacerlo se guarda una copia de lo actual («Antes de
        restaurar» en la lista), así se puede deshacer. Hazlo con el local
        cerrado: lo que se registre durante la restauración se pierde, y al
        terminar todos vuelven a iniciar sesión.
      </p>
      <p style={{ margin: "0 0 12px" }}>
        <Button
          basic
          color="blue"
          loading={saving}
          disabled={saving}
          onClick={onSave}
        >
          <Icon name="save" /> Guardar respaldo ahora en el servidor
        </Button>
        <br />
        <small style={{ color: "#777" }}>
          Queda en la lista como «Manual» (se guardan los últimos 5), lista para
          restaurar.
        </small>
      </p>

      {backups === null ? (
        <Loader active inline />
      ) : backups.length === 0 ? (
        <Message size="small">
          Todavía no hay respaldos guardados en el servidor.
        </Message>
      ) : (
        <>
          <div
            style={{
              maxHeight: 300,
              overflowY: "auto",
              border: "1px solid rgba(34,36,38,.15)",
              borderRadius: 4,
            }}
          >
            <Table compact unstackable style={{ border: 0, margin: 0 }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={STICKY}>Fecha</Table.HeaderCell>
                  <Table.HeaderCell style={STICKY}>Tipo</Table.HeaderCell>
                  <Table.HeaderCell style={STICKY}>Contenido</Table.HeaderCell>
                  <Table.HeaderCell style={STICKY} />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {map(backups, (item) => (
                  <Table.Row key={item.name}>
                    <Table.Cell>{formatWhen(item.created_at)}</Table.Cell>
                    <Table.Cell>{KIND_TEXT[item.kind] || item.kind}</Table.Cell>
                    <Table.Cell>
                      {item.error ? (
                        <span style={{ color: "#db2828" }}>{item.error}</span>
                      ) : (
                        <>
                          {describe(item)}
                          <br />
                          <small style={{ color: "#777" }}>
                            Última venta: {formatWhen(item.last_sale, true)}
                          </small>
                        </>
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <Button
                        size="small"
                        disabled={!!item.error || checking}
                        onClick={() => check({ name: item.name })}
                      >
                        Elegir
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          <small style={{ color: "#777", display: "block", marginTop: 6 }}>
            {backups.length} {backups.length === 1 ? "respaldo" : "respaldos"} ·
            del más nuevo al más viejo
          </small>
        </>
      )}

      <p style={{ margin: "14px 0 8px" }}>
        O sube un respaldo que tengas guardado (archivo .sqlite3):
      </p>
      <label className="ui button" htmlFor="restore-file">
        <Icon name="upload" /> Subir respaldo
      </label>
      <input
        id="restore-file"
        type="file"
        accept=".sqlite3"
        hidden
        onChange={onFile}
      />
      {checking && (
        <Loader active inline size="small" style={{ marginLeft: 12 }} />
      )}

      <Modal
        size="small"
        open={!!candidate}
        onClose={() => !restoring && setCandidate(null)}
      >
        <Modal.Header>¿Restaurar este respaldo?</Modal.Header>
        {candidate && (
          <Modal.Content>
            <p>
              <strong>{candidate.data.name}</strong>
              <br />
              {describe(candidate.data)}
              <br />
              Última venta: {formatWhen(candidate.data.last_sale, true)}
            </p>
            {candidate.data.newer_migrations?.length > 0 ? (
              <Message negative>
                Este respaldo es de una versión más nueva del sistema: no se
                puede restaurar aquí.
              </Message>
            ) : (
              <>
                {candidate.data.pending_migrations?.length > 0 && (
                  <Message info size="small">
                    Es de una versión anterior del sistema: al restaurarlo se
                    actualiza solo.
                  </Message>
                )}
                <Message warning>
                  Se reemplaza <strong>toda</strong> la información actual
                  (pedidos, ventas, caja, carta, mesas y usuarios). Lo actual se
                  guarda antes como «Antes de restaurar».
                </Message>
                <p>
                  Escribe <strong>{CONFIRM_WORD}</strong> para continuar:
                </p>
                <Input
                  fluid
                  autoFocus
                  value={word}
                  onChange={(_, d) => setWord(d.value)}
                  placeholder={CONFIRM_WORD}
                />
              </>
            )}
          </Modal.Content>
        )}
        <Modal.Actions>
          <Button disabled={restoring} onClick={() => setCandidate(null)}>
            Cancelar
          </Button>
          <Button
            negative
            loading={restoring}
            disabled={
              restoring ||
              word.trim() !== CONFIRM_WORD ||
              candidate?.data.newer_migrations?.length > 0
            }
            onClick={onRestore}
          >
            Restaurar
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
